import React, { useCallback, useRef, useState } from 'react';
import './style.scss';

interface Mark {
    value: number;
    label?: string;
}

interface Props {
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step?: number;
    marks?: Mark[];
    label?: ((value: number) => React.ReactNode) | null;
    labelAlwaysOn?: boolean;
    restrictToMarks?: boolean;
    className?: string;
}

export const BaseSlider: React.FunctionComponent<Props> = ({
    value,
    onChange,
    min,
    max,
    step = 1,
    marks,
    label,
    labelAlwaysOn,
    restrictToMarks,
    className,
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [showLabel, setShowLabel] = useState(false);

    const percent = ((value - min) / (max - min)) * 100;

    const snapToMark = useCallback(
        (val: number) => {
            if (!restrictToMarks || !marks || marks.length === 0) return val;
            return marks.reduce((closest, mark) =>
                Math.abs(mark.value - val) < Math.abs(closest - val) ? mark.value : closest,
                marks[0].value,
            );
        },
        [restrictToMarks, marks],
    );

    const getValueFromPosition = useCallback(
        (clientX: number) => {
            const track = trackRef.current;
            if (!track) return value;
            const rect = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            let raw = min + ratio * (max - min);
            raw = Math.round(raw / step) * step;
            raw = Math.max(min, Math.min(max, raw));
            return snapToMark(raw);
        },
        [min, max, step, value, snapToMark],
    );

    const handlePointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        setIsDragging(true);
        const newVal = getValueFromPosition(e.clientX);
        onChange(newVal);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDragging) return;
        const newVal = getValueFromPosition(e.clientX);
        onChange(newVal);
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        setIsDragging(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = Number(e.target.value);
        onChange(snapToMark(raw));
    };

    return (
        <div className={`base-slider${className ? ` ${className}` : ''}`}>
            <div
                className="base-slider__track"
                ref={trackRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                <div
                    className="base-slider__bar"
                    style={{ width: `${percent}%` }}
                />

                {marks?.map((mark) => {
                    const markPercent = ((mark.value - min) / (max - min)) * 100;
                    const isFilled = mark.value <= value;
                    return (
                        <React.Fragment key={mark.value}>
                            <div
                                className={`base-slider__mark${isFilled ? ' base-slider__mark--filled' : ''}`}
                                style={{ left: `${markPercent}%` }}
                            />
                            {mark.label && (
                                <div
                                    className="base-slider__mark-label"
                                    style={{ left: `${markPercent}%` }}
                                >
                                    {mark.label}
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}

                <div
                    className="base-slider__thumb"
                    style={{ left: `${percent}%` }}
                    onMouseEnter={() => setShowLabel(true)}
                    onMouseLeave={() => setShowLabel(false)}
                >
                    {label !== null && label !== undefined && (showLabel || labelAlwaysOn) && (
                        <div className="base-slider__label">
                            {label(value)}
                        </div>
                    )}
                </div>
            </div>

            <input
                type="range"
                className="base-slider__input"
                min={min}
                max={max}
                step={restrictToMarks && marks ? 1 : step}
                value={value}
                onChange={handleInputChange}
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={value}
            />
        </div>
    );
};
